'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import type { AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import { useSnackbar } from '@repo/ui/Snackbar/useSnackbar';
import { useRouter } from 'next/navigation';
import { useAnswerDispatch, useResetAnswers, useSubmittedAnswers } from '@/entities/answer/model/hooks';
import { useSessionSync } from '@/entities/session/model/sessionSyncContext';
import { getBranchForPrimaryGoal, getBranchStep, getNextStepFromPrimaryGoal, getPreviousStepId, resolveNextStepId } from '@/shared/config/funnel/branch';
import type { BranchId } from '@/shared/config/funnel/constants';
import { ENTRY_STEP_ID, FINDER_ENTRY_PATH, RECOMMENDATIONS } from '@/shared/config/funnel/flowConstants';
import { getBranchFromAnswers, getBranchSlug, getStepSlug } from '@/shared/config/funnel/utils/client';
import { withCurrentSearchParams, withCurrentSearchParamsExcept } from '@/shared/lib/searchParams';
import { FUNNEL_INTERRUPTS } from '../config/interrupts';
import { resolveBranchEntryBackTo } from '../lib/navigationUtils';
import { useBranchId, useFunnelConfigVersion, useFunnelFlattenedConfig, useFunnelStepId, useStartSubmitTransition } from './Context/hooks';
import { applyInterruptOutcome } from './interrupts/applyOutcome';
import { useFunnelInterrupt } from './interrupts/Context';
import { getDismissedInterruptIds } from './interrupts/dismissed';
import { findMatchingInterrupt } from './interrupts/match';
import { useFinishFunnel } from './useFinishFunnel';
import { useFunnelPageAction } from './useFunnelPageAction';
export function useFunnelNavigation() {
  const $ = _c(40);
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const funnelConfigVersion = useFunnelConfigVersion();
  const branchId = useBranchId();
  const stepId = useFunnelStepId();
  const submittedAnswers = useSubmittedAnswers();
  const router = useRouter();
  const dispatch = useAnswerDispatch();
  const {
    onAnswerSubmitted: persistSubmittedAnswer,
    abandonRun
  } = useSessionSync();
  const resetAnswers = useResetAnswers();
  const finishFunnel = useFinishFunnel();
  const firePageActionAnalyticsEvent = useFunnelPageAction();
  const snackbar = useSnackbar();
  const {
    beginInterrupt
  } = useFunnelInterrupt();
  const startSubmitTransition = useStartSubmitTransition();
  let t0;
  if ($[0] !== funnelFlattenedConfig || $[1] !== router || $[2] !== snackbar) {
    t0 = (targetStepId, currentBranchId) => {
      snackbar.dismiss();
      const branchStep = getBranchStep(funnelFlattenedConfig, currentBranchId, targetStepId);
      if (!branchStep) {
        datadogRum.addError(new Error("Branch step not found"));
        router.push(FINDER_ENTRY_PATH);
      } else {
        router.push(withCurrentSearchParams(`/credit-cards/cardfinder/${getBranchSlug(currentBranchId)}/${getStepSlug(branchStep.step)}`));
      }
    };
    $[0] = funnelFlattenedConfig;
    $[1] = router;
    $[2] = snackbar;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const goToStep = t0;
  let t1;
  if ($[4] !== branchId || $[5] !== funnelFlattenedConfig || $[6] !== router || $[7] !== stepId || $[8] !== submittedAnswers) {
    t1 = () => {
      if (branchId) {
        const previousStepId = getPreviousStepId(funnelFlattenedConfig, branchId, stepId, submittedAnswers);
        if (previousStepId) {
          router.replace(withCurrentSearchParams(`/credit-cards/cardfinder/${getBranchSlug(branchId)}/${getStepSlug({
            id: previousStepId
          })}`));
          return;
        }
        const backTo = resolveBranchEntryBackTo(new URLSearchParams(window.location.search).get("backTo"));
        router.replace(withCurrentSearchParamsExcept(backTo, ["backTo"]));
        return;
      }
      router.back();
    };
    $[4] = branchId;
    $[5] = funnelFlattenedConfig;
    $[6] = router;
    $[7] = stepId;
    $[8] = submittedAnswers;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const goBack = t1;
  let answersWithSubmission;
  let resumeAfterStep;
  if ($[10] !== finishFunnel || $[11] !== goToStep || $[12] !== snackbar || $[13] !== submittedAnswers) {
    resumeAfterStep = async (currentStepId, currentBranchId_0, next, isLastStep, submittedAnswer) => {
      if (isLastStep) {
        snackbar.dismiss();
        await finishFunnel(currentBranchId_0, answersWithSubmission(currentStepId, submittedAnswer));
        return;
      }
      if (next === undefined) {
        datadogRum.addError(new Error("Next step was not found"), {
          stepId: currentStepId,
          answer: typeof submittedAnswer === "string" ? submittedAnswer : undefined
        });
        return;
      }
      if (next === currentStepId) {
        datadogRum.addError(new Error("Next step is the same as the current step"));
      }
      goToStep(next, currentBranchId_0);
    };
    answersWithSubmission = (currentStepId_0, submittedAnswer_0) => submittedAnswer_0 === undefined ? submittedAnswers : {
      ...submittedAnswers,
      [currentStepId_0]: submittedAnswer_0
    };
    $[10] = finishFunnel;
    $[11] = goToStep;
    $[12] = snackbar;
    $[13] = submittedAnswers;
    $[14] = answersWithSubmission;
    $[15] = resumeAfterStep;
  } else {
    answersWithSubmission = $[14];
    resumeAfterStep = $[15];
  }
  let t2;
  if ($[16] !== abandonRun || $[17] !== answersWithSubmission || $[18] !== funnelConfigVersion || $[19] !== persistSubmittedAnswer) {
    t2 = async (currentStepId_1, submittedAnswer_1) => {
      const result = await persistSubmittedAnswer(answersWithSubmission(currentStepId_1, submittedAnswer_1), funnelConfigVersion);
      if (result !== "version-mismatch") {
        return "continue";
      }
      abandonRun();
      return "abandoned";
    };
    $[16] = abandonRun;
    $[17] = answersWithSubmission;
    $[18] = funnelConfigVersion;
    $[19] = persistSubmittedAnswer;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const saveSubmission = t2;
  let t3;
  if ($[21] !== beginInterrupt || $[22] !== branchId || $[23] !== dispatch || $[24] !== firePageActionAnalyticsEvent || $[25] !== funnelFlattenedConfig || $[26] !== goToStep || $[27] !== resetAnswers || $[28] !== resumeAfterStep || $[29] !== saveSubmission || $[30] !== startSubmitTransition || $[31] !== submittedAnswers) {
    t3 = async (currentStepId_2, submittedAnswer_2) => {
      const routingAnswer = typeof submittedAnswer_2 === "string" ? submittedAnswer_2 : undefined;
      const branchIdFromAnswers = getBranchFromAnswers(funnelFlattenedConfig, submittedAnswers);
      if (!branchId) {
        if (!routingAnswer) {
          datadogRum.addError(new Error("Primary goal should always have a routing answer (single select)."), {
            stepId: currentStepId_2,
            branchId
          });
          return;
        }
        const nextBranchId = getBranchForPrimaryGoal(funnelFlattenedConfig, routingAnswer);
        const nextStep = getNextStepFromPrimaryGoal(funnelFlattenedConfig, routingAnswer);
        if (nextStep === ENTRY_STEP_ID) {
          datadogRum.addError(new Error("Next step is the entry step"), {
            stepId: currentStepId_2,
            branchId,
            answer: routingAnswer
          });
        }
        if (nextStep === currentStepId_2) {
          datadogRum.addError(new Error("Next step is the same as the current step"));
        }
        if (branchIdFromAnswers && branchIdFromAnswers !== nextBranchId) {
          resetAnswers();
        }
        if (nextStep && nextBranchId) {
          dispatch({
            type: "SUBMIT_ANSWER",
            stepId: currentStepId_2,
            value: submittedAnswer_2
          });
          if ((await saveSubmission(currentStepId_2, routingAnswer)) === "abandoned") {
            return;
          }
          firePageActionAnalyticsEvent(currentStepId_2, nextBranchId, submittedAnswer_2);
          goToStep(nextStep, nextBranchId);
        } else {
          datadogRum.addError(new Error("Next branch was not found"), {
            stepId: currentStepId_2,
            answer: routingAnswer
          });
        }
        return;
      }
      const next_0 = resolveNextStepId(funnelFlattenedConfig, branchId, currentStepId_2, routingAnswer);
      const isLastStep_0 = next_0 === RECOMMENDATIONS;
      if (submittedAnswer_2 !== undefined) {
        dispatch({
          type: "SUBMIT_ANSWER",
          stepId: currentStepId_2,
          value: submittedAnswer_2
        });
        if (!isLastStep_0 && (await saveSubmission(currentStepId_2, submittedAnswer_2)) === "abandoned") {
          return;
        }
      }
      firePageActionAnalyticsEvent(currentStepId_2, branchId, submittedAnswer_2);
      const matchingInterrupt = findMatchingInterrupt({
        interrupts: FUNNEL_INTERRUPTS,
        stepId: currentStepId_2,
        branchId,
        submittedAnswer: submittedAnswer_2,
        dismissedIds: getDismissedInterruptIds()
      });
      if (matchingInterrupt) {
        beginInterrupt(matchingInterrupt.id, {
          resume: () => startSubmitTransition(() => resumeAfterStep(currentStepId_2, branchId, next_0, isLastStep_0, submittedAnswer_2)),
          applyOutcome: outcome => applyInterruptOutcome(outcome, {
            dispatch,
            goToStep,
            funnelFlattenedConfig
          })
        });
        return;
      }
      await resumeAfterStep(currentStepId_2, branchId, next_0, isLastStep_0, submittedAnswer_2);
    };
    $[21] = beginInterrupt;
    $[22] = branchId;
    $[23] = dispatch;
    $[24] = firePageActionAnalyticsEvent;
    $[25] = funnelFlattenedConfig;
    $[26] = goToStep;
    $[27] = resetAnswers;
    $[28] = resumeAfterStep;
    $[29] = saveSubmission;
    $[30] = startSubmitTransition;
    $[31] = submittedAnswers;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  const runAdvance = t3;
  let t4;
  if ($[33] !== runAdvance || $[34] !== startSubmitTransition) {
    t4 = (currentStepId_3, submittedAnswer_3) => {
      startSubmitTransition(() => runAdvance(currentStepId_3, submittedAnswer_3));
    };
    $[33] = runAdvance;
    $[34] = startSubmitTransition;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  const advance = t4;
  let t5;
  if ($[36] !== advance || $[37] !== goBack || $[38] !== goToStep) {
    t5 = {
      goToStep,
      goBack,
      advance
    };
    $[36] = advance;
    $[37] = goBack;
    $[38] = goToStep;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  return t5;
}